import {
  doubleTheDonationGetApiKeysInputSchema,
  doubleTheDonationSaveApiKeysInputSchema,
  doubleTheDonationApiKeysGetSuccessSchema,
  doubleTheDonationSaveApiKeysSuccessBodySchema,
  doubleTheDonationHttpResultSchema,
  doubleTheDonationGatewayMessageBodySchema,
  type DoubleTheDonationGetApiKeysInput,
  type DoubleTheDonationSaveApiKeysInput,
} from '@charitystack/org-engagement-contracts';
import { selectDoubleTheDonationUrl } from '../flags/double-the-donation';

/** Missing/disabled rollout configuration retains legacy; a selected request never falls back. */
export function createDoubleTheDonationClient({
  organizationId,
  authorization,
  url = import.meta.env.VITE_APP_CS_DTD_SERVER_URL,
  fetch: send = globalThis.fetch,
}: {
  organizationId?: unknown;
  authorization: string;
  url?: string;
  fetch?: typeof globalThis.fetch;
}) {
  const selectedUrl = selectDoubleTheDonationUrl({ organizationId, authorization });
  const endpoint = `${(selectedUrl ?? url)?.replace(/\/$/, '')}/api-keys`;
  const headers = {
    accept: '*/*',
    'content-type': 'application/json',
    [selectedUrl ? 'X-CS-Authorization' : 'Authorization']: authorization,
  };
  async function readFailure(response: Response, body: unknown) {
    const gateway = doubleTheDonationGatewayMessageBodySchema.safeParse(body);
    if (gateway.success && [401, 403, 404, 500, 503].includes(response.status)) return gateway.data;
    return doubleTheDonationHttpResultSchema.parse({ status: response.status, body }).body;
  }
  return {
    usesNewTransport: selectedUrl !== null,
    async getApiKeys(input: DoubleTheDonationGetApiKeysInput) {
      const query = doubleTheDonationGetApiKeysInputSchema.parse(input);
      const response = await send(`${endpoint}?${new URLSearchParams(query)}`, {
        method: 'GET',
        headers,
      });
      const body: unknown = await response.json();
      return {
        ok: response.status === 200,
        status: response.status,
        body:
          response.status === 200
            ? doubleTheDonationApiKeysGetSuccessSchema.parse(body)
            : await readFailure(response, body),
      };
    },
    async saveApiKeys(
      input: DoubleTheDonationSaveApiKeysInput,
      idempotencyKey?: string,
      { authorization: attemptAuthorization = authorization }: { authorization?: string } = {},
    ) {
      const body = doubleTheDonationSaveApiKeysInputSchema.parse(input);
      if (selectedUrl && !idempotencyKey?.trim()) throw new Error('IDEMPOTENCY_KEY_REQUIRED');
      const response = await send(endpoint, {
        method: 'POST',
        headers: {
          ...headers,
          [selectedUrl ? 'X-CS-Authorization' : 'Authorization']: attemptAuthorization,
          ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
        },
        body: JSON.stringify(body),
      });
      const decoded: unknown = await response.json();
      return {
        ok: response.status === 200,
        status: response.status,
        body:
          response.status === 200
            ? doubleTheDonationSaveApiKeysSuccessBodySchema.parse(decoded)
            : await readFailure(response, decoded),
      };
    },
  };
}
